import { useEffect, useMemo, useState } from 'react';
import { Journeys, useCrossFlow } from '@atlassiansox/cross-flow-support';
import {
	type ContextResolver,
	type QueryResult,
	type AppRecRecommendationResult,
	useRecommendations,
} from '@atlassian/app-recommendations-kit';

import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useSubProduct } from '@atlassian/jira-subproduct';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import type { UseJiraYourWorkExperimentType } from './types';
import { useFireButtonClickEvent, useFireExposureEventOnce } from './utils';

const SOURCE_COMPONENT = 'jiraYourWorkRecommendationsExperiment';
export const JIRA_PRODUCT_DISCOVERY_PRODUCT_ID = 'a6f908c8-709c-45f4-9e2d-eca25b93b94a';
// localStorage keys
const CROSSFLOW_MESSAGE_KEY = 'crossflow_message_is_dismissed';
const EXPANSION_MESSAGE_KEY = 'expansion_message_is_dismissed';
const PRODUCT_PROVISIONED_KEY = 'product_provisioned';
const PRODUCT_PROVISIONED_TIME_STAMP = 'product_provisioned_time_stamp';

type BoolFunc = () => boolean;

export const useJiraYourWorkExperiment = (): UseJiraYourWorkExperimentType => {
	const crossFlow = useCrossFlow();
	const { cloudId, isSiteAdmin, atlassianAccountId } = useTenantContext();
	const subProduct = useSubProduct();
	const contextResolver: ContextResolver = useMemo(
		() => ({
			tenantId: cloudId,
			product: 'jira',
			useCase: 'jira-your-work',
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			subproduct: subProduct as string,
		}),
		[cloudId, subProduct],
	);

	// Message Visibility
	const [isCrossflowMessageVisible, setIsCrossflowMessageVisible] = useState<boolean>(false);
	const [isExpansionMessageVisible, setIsExpansionMessageVisible] = useState<boolean>(false);
	const showCrossflowMessage = () => setIsCrossflowMessageVisible(true);
	const showExpansionMessage = () => setIsExpansionMessageVisible(true);
	const hideCrossflowMessage = () => setIsCrossflowMessageVisible(false);
	const hideExpansionMessage = () => setIsExpansionMessageVisible(false);

	const cohort: string = expVal('jira_your_work_recommendations_nba', 'cohort', 'not-enrolled');

	// Analytic events
	const fireDismissExpansionCardAnalyticsEvent = useFireButtonClickEvent({
		eventName: 'dissmissExpansionCard',
		cohort,
	});
	const fireNotInterestedAnalyticsEvent = useFireButtonClickEvent({
		eventName: 'notInterested',
		cohort,
	});

	// On Dimissal
	const dismissCrossflowMessage = () => {
		fireNotInterestedAnalyticsEvent();
		try {
			localStorage.setItem(CROSSFLOW_MESSAGE_KEY, `${atlassianAccountId}`);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics({
				meta: { id: 'trailblazer.nbaexperiment.dismissCrossflowMessage' },
				error,
			});
		}
		hideCrossflowMessage();
	};
	const dimissExpansionMessage = () => {
		fireDismissExpansionCardAnalyticsEvent();
		try {
			localStorage.setItem(EXPANSION_MESSAGE_KEY, `${atlassianAccountId}`);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics({
				meta: { id: 'trailblazer.nbaexperiment.dimissExpansionMessage' },
				error,
			});
		}
		hideExpansionMessage();
	};

	const onProductProvisionedByUser = () => {
		localStorage.setItem(`${PRODUCT_PROVISIONED_KEY}-${cloudId}`, `${atlassianAccountId}`);
		localStorage.setItem(`${PRODUCT_PROVISIONED_TIME_STAMP}-${cloudId}`, `${Date.now()}`);
		hideCrossflowMessage();
	};

	const isCrossflowMessageDismissed: BoolFunc = () =>
		localStorage.getItem(CROSSFLOW_MESSAGE_KEY) === atlassianAccountId;

	const isExpansionMessageDismissed: BoolFunc = () =>
		localStorage.getItem(EXPANSION_MESSAGE_KEY) === atlassianAccountId;

	const fireExposureEventOnce = useFireExposureEventOnce();

	const isProductProvisionedByUser: BoolFunc = () => {
		const productInstalledByUserId = localStorage.getItem(`${PRODUCT_PROVISIONED_KEY}-${cloudId}`);
		if (productInstalledByUserId && productInstalledByUserId === atlassianAccountId) {
			const now = Date.now();
			const twoDays = 2 * 24 * 60 * 60 * 1000;
			const productProvisioningTime = Number(
				localStorage.getItem(`${PRODUCT_PROVISIONED_TIME_STAMP}-${cloudId}`),
			);
			const twoDaysAfterProvisioning = twoDays + productProvisioningTime;
			return twoDaysAfterProvisioning < now;
		}
		return false;
	};

	const { getRecommendations } = useRecommendations({
		contextResolver,
	});

	useEffect(() => {
		const getJpdStatus = async () => {
			try {
				const recommendations: QueryResult<AppRecRecommendationResult> =
					await getRecommendations(3);

				if (recommendations.response && recommendations.response.data) {
					let isExpansionCardVisible = false;
					let isCrossflowCardVisible = false;
					// JPD will not be recommended if JPD is installed on site or user dismissed it
					const isJpdRecommended: boolean = recommendations.response.data.some(
						(recommendation) => recommendation.entityId === JIRA_PRODUCT_DISCOVERY_PRODUCT_ID,
					);
					if (isJpdRecommended) {
						showCrossflowMessage();
						isCrossflowCardVisible = true;
					} else if (
						!isCrossflowMessageDismissed() &&
						!isExpansionMessageDismissed() &&
						isProductProvisionedByUser()
					) {
						showExpansionMessage();
						isExpansionCardVisible = true;
					}

					const cardVersion = getCardVersionName({
						cohort,
						isCrossflowMessageVisible: isCrossflowCardVisible,
						isExpansionMessageVisible: isExpansionCardVisible,
					});

					fireExposureEventOnce({ cohort, cardVersion });
				} else {
					fireExposureEventOnce({ cohort, cardVersion: 'null-card-missing-data' });
				}
			} catch (error) {
				fireExposureEventOnce({ cohort, cardVersion: 'null-card-error' });
			}
		};

		if (cohort === 'experiment') {
			getJpdStatus();
		} else if (cohort === 'control') {
			fireExposureEventOnce({ cohort, cardVersion: 'null-card-control' });
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onLearnMoreClick = async () => {
		if (crossFlow.isEnabled) {
			try {
				const crossflowResult = await crossFlow.api.open({
					journey: Journeys.DECIDE,
					sourceComponent: SOURCE_COMPONENT,
					sourceContext: 'learnMore',
					targetProduct: 'jira-product-discovery',
				});

				if (crossflowResult.success === true) {
					// save time stamp in local storage after product is installed
					onProductProvisionedByUser();
				}
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: { id: 'trailblazer.nbaexperiment.onLearnMoreClick' },
					error,
				});
			}
		}
	};

	const openCrossFlow = async () => {
		if (crossFlow.isEnabled) {
			try {
				const crossflowResult = await crossFlow.api.open({
					journey: Journeys.GET_STARTED,
					sourceComponent: SOURCE_COMPONENT,
					sourceContext: 'tryNow',
					targetProduct: 'jira-product-discovery',
				});

				if (crossflowResult.success === true) {
					// save time stamp in local storage after product is installed
					onProductProvisionedByUser();
				}
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: { id: 'trailblazer.nbaexperiment.openCrossFlow' },
					error,
				});
			}
		}
	};

	return {
		cloudId,
		isSiteAdmin,
		openCrossFlow,
		isCrossflowMessageVisible,
		isExpansionMessageVisible,
		dismissCrossflowMessage,
		dimissExpansionMessage,
		contextResolver,
		cohort,
		onLearnMoreClick,
	};
};

type GetCardVersioNameProps = {
	isCrossflowMessageVisible: boolean;
	isExpansionMessageVisible: boolean;
	cohort: string;
};

const getCardVersionName = ({
	isCrossflowMessageVisible,
	isExpansionMessageVisible,
	cohort,
}: GetCardVersioNameProps) => {
	if (cohort === 'control') {
		return 'null-card-control';
	}

	if (isCrossflowMessageVisible) {
		return 'cross-flow';
	}

	if (isExpansionMessageVisible) {
		return 'expansion';
	}

	// between cross flow and expansion period
	return 'null-card-waiting-period';
};
